import React from 'react';
import GooglePreview from './GooglePreview';
import { SEOData } from '../types';

interface SEOPanelProps {
    seoTitle: string;
    seoDescription: string;
    seoKeywords: string;
    ogTitle: string;
    ogDescription: string;
    canonicalUrl: string;
    // seoData: SEOData; // REMOVED
    seoScore?: number;
    slug: string;
    contentForAI: string;
    onChange: (fields: { seoTitle?: string; seoDescription?: string; seoKeywords?: string; ogTitle?: string; ogDescription?: string; canonicalUrl?: string; seoScore?: number }) => void;
    baseUrl?: string;
    compact?: boolean;
}

const SEOPanel: React.FC<SEOPanelProps> = ({
    seoTitle,
    seoDescription,
    seoKeywords,
    ogTitle,
    ogDescription,
    canonicalUrl,
    seoScore,
    slug,
    contentForAI,
    onChange,
    baseUrl = 'https://vanity.com',
    compact = false
}) => {
    const handleKeywordsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ seoKeywords: e.target.value });
    };

    // Color code for score
    const getScoreColor = (s: number) => {
        if (s >= 80) return 'bg-green-500';
        if (s >= 50) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    return (
        <div className={`flex flex-col ${compact ? 'gap-6' : 'gap-12'}`}>
            {!compact && (
                <div className="flex items-center justify-between border-b border-gray-100 pb-6">
                    <div>
                        <h2 className="text-2xl font-black text-gray-900 tracking-tight mb-1">Search Engine Optimization</h2>
                        <p className="text-gray-500 text-sm">Control how this content appears on search engines and social media.</p>
                    </div>
                </div>
            )}

            {/* SCORE BAR */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <div className="flex justify-between items-end mb-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">SEO Score</label>
                    <span className="text-2xl font-black text-gray-900">{seoScore || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                    <div
                        className={`h-2.5 rounded-full transition-all duration-1000 ${getScoreColor(seoScore || 0)}`}
                        style={{ width: `${seoScore || 0}%` }}
                    ></div>
                </div>
            </div>

            <div className={`flex flex-col ${compact ? 'gap-6' : 'gap-12'}`}>
                {/* Inputs */}
                <div className={compact ? 'space-y-4' : 'space-y-8'}>

                    {/* META TITLE */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">Meta Title</label>
                        <input
                            type="text"
                            value={seoTitle || ''}
                            onChange={(e) => onChange({ seoTitle: e.target.value })}
                            placeholder="Enter a compelling title..."
                            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-vanity-black/5 transition-all"
                        />
                        <p className="text-[10px] text-gray-400 text-right">{seoTitle?.length || 0} / 60</p>
                    </div>

                    {/* META DESCRIPTION */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">Meta Description</label>
                        <textarea
                            value={seoDescription || ''}
                            onChange={(e) => onChange({ seoDescription: e.target.value })}
                            placeholder="Describe this content for search results..."
                            rows={4}
                            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-vanity-black/5 transition-all resize-none"
                        />
                        <p className="text-[10px] text-gray-400 text-right">{seoDescription?.length || 0} / 160</p>
                    </div>

                    {/* SEO KEYWORDS */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">Keywords (Comma Separated)</label>
                        <input
                            type="text"
                            value={seoKeywords || ''}
                            onChange={handleKeywordsChange}
                            placeholder="luxury, lifestyle, vienna..."
                            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-vanity-black/5 transition-all"
                        />
                    </div>

                    <div className="border-t border-gray-100 my-6 pt-6">
                        <h4 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-6">Social & OG Tags</h4>

                        {/* OG TITLE */}
                        <div className="space-y-2 mb-4">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">OG Title</label>
                            <input
                                type="text"
                                value={ogTitle || ''}
                                onChange={(e) => onChange({ ogTitle: e.target.value })}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-vanity-black/5 transition-all"
                            />
                        </div>

                        {/* OG DESCRIPTION */}
                        <div className="space-y-2 mb-4">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">OG Description</label>
                            <textarea
                                value={ogDescription || ''}
                                onChange={(e) => onChange({ ogDescription: e.target.value })}
                                rows={2}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-vanity-black/5 transition-all resize-none"
                            />
                        </div>

                        {/* CANONICAL URL */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">Canonical URL</label>
                            <input
                                type="text"
                                value={canonicalUrl || ''}
                                onChange={(e) => onChange({ canonicalUrl: e.target.value })}
                                placeholder="https://vanity.co.at/..."
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-vanity-black/5 transition-all"
                            />
                        </div>
                    </div>
                </div>

                {/* Preview (Now at the bottom) */}
                {!compact && (
                    <div className="border-t border-gray-100 pt-8 mt-4">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1 mb-6 block">Google Search Preview</label>
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <GooglePreview
                                title={seoTitle}
                                description={seoDescription}
                                slug={slug}
                                baseUrl={baseUrl}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SEOPanel;
